import {html, css, CSSResultGroup, TemplateResult} from 'lit'
import {customElement, property, state} from 'lit/decorators.js'
import {classMap} from 'lit-html/directives/class-map.js'
import {BaseComponent} from './base-component.js'

export interface TabOption {
  label: string
  key: string
}

@customElement('il-tabs')
export default class TabsComponent extends BaseComponent {
  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-tabs {
        display: flex;
        gap: 1rem;
      }

      .il-tab {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1 1 auto;
        padding: .75rem 1rem;
        border: none;
        border-bottom: 2px solid var(--il-color-neutrals-0);
        background-color: var(--il-color-neutrals-0);
        border-radius: 0;
        cursor: pointer;
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-16);
        font-weight: var(--il-font-weight-xbold);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-tab.il-tab--selected {
        color: var(--il-color-accent-300);
        border-color: var(--il-color-accent-300);
      }

      .il-tab:not(.il-tab--selected):hover {
        color: var(--il-color-neutrals-350);
      }

      .il-tab:focus-visible {
        border-bottom: 2px solid var(--il-color-neutrals-0);
        border-radius: 6px;
        outline: 2px solid var(--il-color-accent-200);
      }

      @media (min-width: 640px) {
        .il-tab {
          flex: 0 1 auto;
        }
      }
    `,
  ]

  @property({type: Array})
  tabs: TabOption[] = []

  @property({attribute: 'initial-selected', type: String})
  initialSelected: string = ''

  @property({type: Function})
  onClick?: (tab: string) => void

  @state()
  selected: string = ''

  get currentSelected (): string {
    return this.selected || this.initialSelected || this.tabs[0]?.key
  }

  private tabClasses (tab: string): {[x: string]: boolean} {
    return {
      'il-tab': true,
      'il-tab--selected': tab === this.currentSelected,
    }
  }

  tabComponent ({label, key}: TabOption): TemplateResult {
    return html`
      <button
        class="${classMap(this.tabClasses(key))}"
        @click="${this._onClick.bind(this, key)}"
        data-test-iliad-tabs-item="${key}"
      >
        ${label}
      </button>
    `
  }

  override render () {
    return html`
      <div class="${this.baseCSSClass} il-tabs" data-test-iliad-tabs>
        ${this.tabs.map((tab: TabOption) => {
          return this.tabComponent(tab)
        })}
      </div>
    `
  }

  private _onClick (tab: string): void {
    this.selected = tab

    this.onClick?.(tab)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-tabs': TabsComponent
  }
}
